import { useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelOrders,
  emergencyClose,
  fetchDashboard,
  fetchOrders,
  pauseEntries,
  resumeEntries,
  runNow,
  startStrategy,
} from '../api/endpoints';
import { errorMessageRu } from '../api/errors';
import { queryKeys } from '../api/query-keys';
import type { CommandKind, CommandResult, DashboardResponse, Order } from '../api/schemas';
import { useSession } from '../auth/session-context';
import { withReauth } from '../auth/with-reauth';
import { Banner } from '../components/Banner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { KeyValue } from '../components/KeyValue';
import { Quantity, Usdt } from '../components/Money';
import { QueryState } from '../components/QueryState';
import { Section } from '../components/Section';
import { StatusPill } from '../components/StatusPill';
import {
  CANCELLABLE_ORDER_STATUSES,
  MODE_DESCRIPTION,
  MODE_LABEL,
  SIDE_LABEL,
  STRATEGY_STATE_HINT,
  STRATEGY_STATE_LABEL,
  STRATEGY_STATE_TONE,
  venueConfirmationRu,
} from '../domain/labels';
import { formatDateTime } from '../lib/datetime';
import { newIdempotencyKey } from '../lib/idempotency';

/**
 * Раздел 5 — «Управление».
 *
 * Confirmation policy, deliberately asymmetric:
 *
 *  - «Приостановить входы» is one click. It only ever *restricts* trading, and
 *    an operator reaching for it is usually in a hurry.
 *  - Everything that can move money — start, resume, run now, emergency close,
 *    cancel orders — goes through the two-step dialog, which names the concrete
 *    action and object, lists the consequences, and requires the confirmation
 *    word to be typed.
 *
 * Every command carries an `Idempotency-Key` created when the dialog opens, so
 * a retry after a re-auth or a flaky network is provably the same command and
 * not a second one. Success is only reported once the server answers; the
 * venue-confirmation flag from that answer is shown verbatim.
 */

interface CommandDescriptor {
  readonly id: CommandKind;
  readonly buttonLabel: string;
  readonly title: string;
  readonly action: string;
  readonly consequences: readonly string[];
  readonly confirmWord: string;
  readonly confirmLabel: string;
  readonly tone: 'danger' | 'primary';
  readonly run: (idempotencyKey: string) => Promise<CommandResult>;
}

interface PendingCommand {
  readonly descriptor: CommandDescriptor;
  readonly idempotencyKey: string;
}

export function ControlPage(): ReactElement {
  const dashboardQuery = useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: ({ signal }) => fetchDashboard(signal),
  });
  const ordersQuery = useQuery({
    queryKey: queryKeys.orders,
    queryFn: ({ signal }) => fetchOrders(signal),
  });

  return (
    <>
      <div className="page-head">
        <h1>Управление</h1>
        <span className="page-head__hint">
          Команды выполняются на площадке. Результат показывается только после подтверждения
          сервером.
        </span>
      </div>
      <QueryState query={dashboardQuery} what="состояние для управления">
        {(dashboard) => (
          <ControlPanel dashboard={dashboard} orders={ordersQuery.data?.items ?? []} />
        )}
      </QueryState>
    </>
  );
}

function ControlPanel({
  dashboard,
  orders,
}: {
  dashboard: DashboardResponse;
  orders: readonly Order[];
}): ReactElement {
  const queryClient = useQueryClient();
  const { canOperate, promptReauth } = useSession();
  const [pending, setPending] = useState<PendingCommand | null>(null);
  const [result, setResult] = useState<CommandResult | null>(null);
  const [commandErrorRu, setCommandErrorRu] = useState<string | null>(null);

  const openOrders = orders.filter((order) => CANCELLABLE_ORDER_STATUSES.has(order.status));

  const mutation = useMutation({
    mutationFn: ({ descriptor, idempotencyKey }: PendingCommand) =>
      withReauth(() => descriptor.run(idempotencyKey), promptReauth),
    onSuccess: async (commandResult) => {
      setResult(commandResult);
      setCommandErrorRu(null);
      setPending(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders });
      await queryClient.invalidateQueries({ queryKey: queryKeys.position });
    },
    onError: (error: unknown) => {
      setCommandErrorRu(errorMessageRu(error));
    },
  });

  const descriptors: readonly CommandDescriptor[] = [
    {
      id: 'START_STRATEGY',
      buttonLabel: 'Запустить стратегию',
      title: 'Запуск стратегии',
      action:
        'Стратегия перейдёт в состояние «Работает» и начнёт выполнять плановые решения по расписанию.',
      consequences: [
        'Со следующего планового времени движок сможет открывать новые позиции.',
        'Действие будет записано в аудит с вашим именем.',
      ],
      confirmWord: 'ЗАПУСК',
      confirmLabel: 'Запустить стратегию',
      tone: 'primary',
      run: startStrategy,
    },
    {
      id: 'RESUME_ENTRIES',
      buttonLabel: 'Возобновить входы',
      title: 'Возобновление входов',
      action: 'Запрет на новые входы будет снят.',
      consequences: [
        'Ближайшее плановое решение снова сможет открыть позицию.',
        'Уже действующие ограничения риска и импульсная пауза сохраняются.',
      ],
      confirmWord: 'ВОЗОБНОВИТЬ',
      confirmLabel: 'Возобновить входы',
      tone: 'primary',
      run: resumeEntries,
    },
    {
      id: 'RUN_NOW',
      buttonLabel: 'Выполнить решение сейчас',
      title: 'Внеплановое выполнение решения',
      action:
        'Движок немедленно оценит рынок и, если правила разрешат вход, откроет реальную позицию.',
      consequences: [
        'Это не предпросмотр: при разрешающем сигнале будет отправлен ордер на площадку.',
        'Для расчёта без сделки используйте «Предпросмотр решения» в разделе «Стратегия».',
        'Дневной лимит одного входа продолжает действовать.',
      ],
      confirmWord: 'ВЫПОЛНИТЬ',
      confirmLabel: 'Выполнить решение сейчас',
      tone: 'danger',
      run: runNow,
    },
    {
      id: 'EMERGENCY_CLOSE',
      buttonLabel: 'Аварийно закрыть позицию',
      title: 'Аварийное закрытие позиции',
      action:
        'Открытая позиция будет закрыта по рынку, защитные ордера сняты, входы заблокированы до конца текущей сессии.',
      consequences: [
        'Закрытие по рынку исполняется с проскальзыванием, убыток может отличаться от расчётного.',
        'Take Profit и Stop Loss будут отменены.',
        'В эту локальную дату новые входы выполняться не будут.',
      ],
      confirmWord: 'ЗАКРЫТЬ',
      confirmLabel: 'Закрыть позицию по рынку',
      tone: 'danger',
      run: emergencyClose,
    },
    {
      id: 'CANCEL_ORDERS',
      buttonLabel: 'Отменить открытые ордера',
      title: 'Отмена открытых ордеров',
      action: 'Все ордера в работе, включая защитные, будут отменены на площадке.',
      consequences: [
        'Позиция останется открытой без Stop Loss и Take Profit.',
        'До восстановления защиты позиция ничем не ограничена, кроме ликвидации.',
        'Восстановить защитные ордера нужно вручную или перезапуском стратегии.',
      ],
      confirmWord: 'ОТМЕНИТЬ',
      confirmLabel: 'Отменить все ордера',
      tone: 'danger',
      run: cancelOrders,
    },
  ];

  const pauseMutation = useMutation({
    mutationFn: () => withReauth(() => pauseEntries(newIdempotencyKey()), promptReauth),
    onSuccess: async (commandResult) => {
      setResult(commandResult);
      setCommandErrorRu(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
    onError: (error: unknown) => {
      setCommandErrorRu(errorMessageRu(error));
    },
  });

  const openDialog = (descriptor: CommandDescriptor): void => {
    setCommandErrorRu(null);
    setResult(null);
    // One key per intent: reopening the dialog is a new command, retrying
    // inside it is the same one.
    setPending({ descriptor, idempotencyKey: newIdempotencyKey() });
  };

  const target = <CommandTarget dashboard={dashboard} openOrderCount={openOrders.length} />;

  return (
    <div className="stack">
      {!canOperate && (
        <Banner tone="warn" title="Только просмотр.">
          Ваша роль не позволяет выполнять команды. Кнопки отключены.
        </Banner>
      )}

      <Section title="Текущее состояние" hint="Проверьте объект команды перед выполнением.">
        <div className="stack stack--tight">
          <div className="row">
            <StatusPill tone={STRATEGY_STATE_TONE[dashboard.strategyState]} srPrefix="Стратегия">
              {STRATEGY_STATE_LABEL[dashboard.strategyState]}
            </StatusPill>
            <StatusPill tone={dashboard.mode === 'LIVE' ? 'danger' : 'neutral'} srPrefix="Режим">
              {MODE_LABEL[dashboard.mode]} — {MODE_DESCRIPTION[dashboard.mode]}
            </StatusPill>
          </div>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            {STRATEGY_STATE_HINT[dashboard.strategyState]}
          </p>
          {target}
        </div>
      </Section>

      {result !== null && <CommandOutcome result={result} />}

      {commandErrorRu !== null && pending === null && (
        <Banner tone="danger" title="Команда не выполнена.">
          {commandErrorRu}
        </Banner>
      )}

      <Section
        title="Быстрая защита"
        hint="Единственная команда без подтверждения: она только запрещает новые входы."
      >
        <div className="btn-row">
          <button
            type="button"
            className="btn"
            disabled={!canOperate || pauseMutation.isPending}
            onClick={() => {
              pauseMutation.mutate();
            }}
          >
            {pauseMutation.isPending ? 'Выполняется…' : 'Приостановить новые входы'}
          </button>
        </div>
        <p className="field__hint" style={{ marginTop: 'var(--s-2)' }}>
          Мониторинг позиции и защитные ордера продолжают работать.
        </p>
      </Section>

      <Section title="Команды стратегии">
        <div className="btn-row">
          {descriptors
            .filter((descriptor) => descriptor.tone === 'primary')
            .map((descriptor) => (
              <button
                key={descriptor.id}
                type="button"
                className="btn btn--primary"
                disabled={!canOperate}
                onClick={() => {
                  openDialog(descriptor);
                }}
              >
                {descriptor.buttonLabel}
              </button>
            ))}
        </div>
      </Section>

      <Section
        title="Опасные команды"
        tone="danger"
        hint="Каждая команда требует двухэтапного подтверждения и записывается в аудит."
      >
        <div className="btn-row">
          {descriptors
            .filter((descriptor) => descriptor.tone === 'danger')
            .map((descriptor) => (
              <button
                key={descriptor.id}
                type="button"
                className="btn btn--danger"
                disabled={
                  !canOperate ||
                  (descriptor.id === 'EMERGENCY_CLOSE' && dashboard.position === null) ||
                  (descriptor.id === 'CANCEL_ORDERS' && openOrders.length === 0)
                }
                onClick={() => {
                  openDialog(descriptor);
                }}
              >
                {descriptor.buttonLabel}
              </button>
            ))}
        </div>
        {dashboard.position === null && (
          <p className="field__hint" style={{ marginTop: 'var(--s-2)' }}>
            Аварийное закрытие недоступно: открытой позиции нет.
          </p>
        )}
        {openOrders.length === 0 && (
          <p className="field__hint">Отмена ордеров недоступна: активных ордеров нет.</p>
        )}
      </Section>

      {pending !== null && (
        <ConfirmDialog
          open
          title={pending.descriptor.title}
          action={pending.descriptor.action}
          target={target}
          consequences={pending.descriptor.consequences}
          confirmWord={pending.descriptor.confirmWord}
          confirmLabel={pending.descriptor.confirmLabel}
          tone={pending.descriptor.tone}
          busy={mutation.isPending}
          errorRu={commandErrorRu}
          idempotencyKey={pending.idempotencyKey}
          onCancel={() => {
            setPending(null);
            setCommandErrorRu(null);
          }}
          onConfirm={() => {
            mutation.mutate(pending);
          }}
        />
      )}
    </div>
  );
}

/**
 * The banner shown after a command.
 *
 * `venueConfirmed` is tri-state, and the three cases really are three different
 * messages: confirmed by the venue, reached the venue with no answer (go and
 * reconcile), and never touches the venue at all. Collapsing the last two into
 * one warning is what the previous version did, which meant «Приостановить
 * входы» — the safest command in the panel — always finished with a warning
 * telling the operator to wait for a confirmation that was never coming.
 */
function CommandOutcome({ result }: { result: CommandResult }): ReactElement {
  const confirmation = venueConfirmationRu(result.venueConfirmed);
  return (
    <Banner tone={confirmation.tone} title={confirmation.titleRu}>
      {result.resultRu} Состояние стратегии: {STRATEGY_STATE_LABEL[result.strategyState]}.
      {confirmation.detailRu === '' ? null : ` ${confirmation.detailRu}`}
      {result.replayed &&
        ' Эта команда уже выполнялась с тем же ключом идемпотентности; показан результат первой попытки, повторно она не выполнялась.'}
    </Banner>
  );
}

function CommandTarget({
  dashboard,
  openOrderCount,
}: {
  dashboard: DashboardResponse;
  openOrderCount: number;
}): ReactElement {
  const position = dashboard.position;
  return (
    <KeyValue
      rows={[
        { label: 'Площадка и инструмент', value: `${dashboard.symbol}` },
        {
          label: 'Режим',
          value: `${MODE_LABEL[dashboard.mode]} — ${MODE_DESCRIPTION[dashboard.mode]}`,
        },
        {
          label: 'Позиция',
          value:
            position === null ? (
              'открытой позиции нет'
            ) : (
              <span>
                {SIDE_LABEL[position.side]}, <Quantity value={position.quantity} />, плечо{' '}
                {position.leverage}x, маржа <Usdt value={position.marginAmount} />
              </span>
            ),
        },
        {
          label: 'Открыта',
          value: position === null ? '—' : formatDateTime(position.openedAt),
        },
        { label: 'Активных ордеров', value: String(openOrderCount) },
      ]}
    />
  );
}
